// =============================================================================
// Admin Analytics — DrilldownDialog (client-side)
// =============================================================================
// The raw-data drill-down for widgets whose full row set is already in the
// browser (the Activation tab): client-side filtering, pagination, and CSV
// export of the filtered set. Reports-tab drilldowns page on the server — see
// `ServerDrilldownDialog`.
//
// When `filterFields` are supplied, the same config drives both the filter
// controls and the CSV column projection — one source of truth per table.
// =============================================================================
import { useEffect, useMemo, useState } from "react";

import { CalendarDateRange, Column } from "@fronterahealth/frontera-ui-components";

import {
  DrilldownFilterField,
  FacetedFilterBar,
  FilterValues,
  applyDrilldownFilters,
} from "@pages/AdminAnalytics/components/DrilldownFilters";
import { DrilldownLayout } from "@pages/AdminAnalytics/components/DrilldownLayout";
import { exportRowsToCsv } from "@pages/AdminAnalytics/helpers";
import { PAGE_SIZE } from "@pages/VideoAnalytics/helpers";

// Re-exported so existing view configs keep importing the type from here.
export type { DrilldownFilterField } from "@pages/AdminAnalytics/components/DrilldownFilters";

interface DrilldownDialogProps<T> {
  title: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  data: T[];
  columns: Column<T>[];
  /** Optional content rendered above the table (e.g. a legend or summary line). */
  header?: React.ReactNode;
  loading?: boolean;
  /** When provided, enables the per-field filter toolbar and CSV export. */
  filterFields?: DrilldownFilterField<T>[];
  /** File name (without extension) for the CSV export. Defaults to the title. */
  exportFileName?: string;
  /**
   * Filters to seed each time the dialog opens — used when a specific widget
   * element (a bar, a status, a row) is clicked to show the raw inputs behind
   * it. Applied on the open transition; the user can then adjust or clear them.
   */
  initialFilters?: FilterValues;
  /** When set, table rows become clickable and invoke this callback (e.g. navigate to the row's record). */
  onRowClick?: (row: T) => void;
  /**
   * The active date range shown as a chip inside the filter bar. Users can change
   * or clear it without leaving the dialog; this is independent of global filters.
   */
  dateRange?: CalendarDateRange;
  /** Called when the user picks a new date range or clears (undefined = reset to widget default). */
  onDateRangeChange?: (r: CalendarDateRange | undefined) => void;
}

export const DrilldownDialog = <T,>({
  title,
  open,
  setOpen,
  data,
  columns,
  header,
  loading,
  filterFields,
  exportFileName,
  initialFilters,
  onRowClick,
  dateRange,
  onDateRangeChange,
}: DrilldownDialogProps<T>) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [filters, setFilters] = useState<FilterValues>({});

  // Seed the filters from the clicked element each time the dialog opens.
  useEffect(() => {
    if (open) setFilters(initialFilters ?? {});
  }, [open, initialFilters]);

  const filtered = useMemo(
    () => (filterFields?.length ? applyDrilldownFilters(data, filterFields, filters) : data),
    [data, filterFields, filters],
  );

  // Any filter change (or reopening) returns to the first page so results are visible.
  useEffect(() => setPage(1), [filters, open]);

  // Slice to the current page ourselves; the Table renders exactly what we pass.
  const pagedData = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  const hasFilters = Boolean(filterFields?.length);
  const showFilterArea = hasFilters || !!onDateRangeChange;

  return (
    <DrilldownLayout
      title={title}
      open={open}
      setOpen={setOpen}
      header={header}
      filterBar={
        showFilterArea ? (
          <FacetedFilterBar
            fields={filterFields ?? []}
            data={data}
            values={filters}
            onChange={setFilters}
            dateValue={dateRange}
            onDateChange={onDateRangeChange}
          />
        ) : undefined
      }
      summary={
        <>
          {filtered.length} of {data.length} {data.length === 1 ? "row" : "rows"}
          {filtered.length !== data.length ? " (filtered)" : ""}
        </>
      }
      onExport={hasFilters ? () => exportRowsToCsv(exportFileName ?? title, filterFields!, filtered) : undefined}
      exportDisabled={filtered.length === 0}
      rows={pagedData}
      columns={columns}
      loading={loading}
      totalItemCount={filtered.length}
      page={page}
      pageSize={pageSize}
      onPageChange={(current, itemsPerPage) => {
        setPage(current);
        setPageSize(itemsPerPage);
      }}
      onRowClick={onRowClick}
    />
  );
};
